import { createFileRoute } from "@tanstack/react-router";
import { format, formatDistanceToNowStrict } from "date-fns";
import { useState } from "react";
import { HelpCircle } from "lucide-react";
import { toast } from "sonner";

import {
  useCreateOpenQuestion,
  useOpenQuestionsList,
  useUpdateOpenQuestion,
} from "@/application/use-cases/open-questions/use-open-questions.queries";
import { PageExplainer } from "@/presentation/components/shell/page-explainer";
import { RouteHero } from "@/presentation/components/shell/route-hero";
import { Button } from "@monorepo-apps/ui/src/components/button";
import { Input } from "@monorepo-apps/ui/src/components/input";
import { StatusDot, STATUS_LABEL } from "@/presentation/components/status/status-dot";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@monorepo-apps/ui/src/components/select";
import type { Status } from "@/infrastructure/supabase/database.types";

export const Route = createFileRoute("/_app/open-questions/")({
  component: OpenQuestionsListPage,
});

function OpenQuestionsListPage() {
  const listQ = useOpenQuestionsList();
  const create = useCreateOpenQuestion();
  const update = useUpdateOpenQuestion();
  const [draft, setDraft] = useState("");

  async function onAdd(e: React.FormEvent) {
    e.preventDefault();
    if (!draft.trim()) return;
    try {
      await create.mutateAsync({
        question: draft.trim(),
        owner_id: null,
        due_date: null,
        status: "open",
        answer: null,
        related_entity_type: null,
        related_entity_id: null,
      });
      setDraft("");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed");
    }
  }

  const items = listQ.data ?? [];
  const open = items.filter((q) => q.status !== "resolved");
  const active = items.filter((q) => q.status === "active");
  const blocked = items.filter((q) => q.status === "blocked");
  const resolved = items.filter((q) => q.status === "resolved");
  const oldestOpen = open[open.length - 1];

  return (
    <div className="animate-page-enter mx-auto flex w-full max-w-7xl flex-col gap-8 p-6 lg:gap-10 lg:p-10">
      <div className="self-end print:hidden">
        <PageExplainer pageKey="open-questions" />
      </div>
      <RouteHero
        eyebrow="Inbox"
        icon={HelpCircle}
        numeral={String(open.length).padStart(2, "0")}
        numeralTone="amber"
        numeralSuffix={`/ ${String(items.length).padStart(2, "0")}`}
        title="Awaiting answers"
        summary="Questions surface in interviews and reviews. Each one is a decision the cycle still owes."
        meta={[
          { label: "Open", value: open.length - active.length - blocked.length, tone: "amber" },
          { label: "Active", value: active.length, tone: "amber" },
          { label: "Blocked", value: blocked.length, tone: "red" },
          {
            label: "Oldest",
            value: oldestOpen ? formatDistanceToNowStrict(new Date(oldestOpen.created_at)) : "—",
          },
        ]}
      />

      <form onSubmit={onAdd} className="flex items-center gap-2 print:hidden">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Capture an open question…"
          className="flex-1"
        />
        <Button type="submit" size="sm" disabled={!draft.trim() || create.isPending}>
          Add
        </Button>
      </form>

      <div>
        {items.length === 0 ? (
          <p className="text-muted-foreground py-8 text-xs italic">
            No open questions yet — they get logged as they surface in stakeholder interviews this
            week.
          </p>
        ) : (
          <ul className="glow-card-static flex flex-col p-2">
            {[...open, ...resolved].map((q) => (
              <li
                key={q.id}
                className="border-border/40 flex items-start gap-3 border-t px-3 py-3 first:border-t-0 print:break-inside-avoid"
              >
                <div className="mt-1.5">
                  <StatusDot tone={q.status} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs leading-relaxed">{q.question}</p>
                  <div className="text-muted-foreground font-data mt-1 flex items-center gap-2 text-[10px] tabular-nums">
                    <span>{STATUS_LABEL[q.status]}</span>
                    <span>·</span>
                    <span>
                      {formatDistanceToNowStrict(new Date(q.created_at), { addSuffix: true })}
                    </span>
                    {q.due_date ? (
                      <>
                        <span>·</span>
                        <span>due {format(new Date(q.due_date), "d MMM")}</span>
                      </>
                    ) : null}
                  </div>
                </div>
                <Select
                  value={q.status}
                  onValueChange={(v) => update.mutate({ id: q.id, patch: { status: v as Status } })}
                >
                  <SelectTrigger className="h-7 w-[110px] text-[10px] print:hidden">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="blocked">Blocked</SelectItem>
                  </SelectContent>
                </Select>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
